/**
 * AgentSession — stateful wrapper over any IAgentRuntime-compatible object.
 *
 * Provides:
 *   - AgentEvent → OutputLine conversion with streaming text accumulation
 *   - State machine: starting → idle ↔ active ↔ waiting → stopped
 *   - Circular output buffer (500 lines, see {@link MAX_OUTPUT_LINES})
 *   - send() routes to prompt() or reply() based on current state
 *   - stop() disposes the runtime and fires optional cleanup
 *   - onChange callback for reactive UIs (React forceUpdate, EventEmitter, etc.)
 *
 * Works with LocalRuntime (in-process) and AgentClient (remote/WebSocket).
 * Extend to add identity fields (id, name, profileName…):
 *
 *   class MySession extends AgentSession {
 *     constructor(readonly id: string, runtime: LocalRuntime | AgentClient) {
 *       super(runtime);
 *     }
 *   }
 *
 * @docLink packages/sdk/concepts#agent-session
 */

import type { AgentEvent } from "@skaile/workspaces/types";
import type { OutputLine, SessionState } from "./types.js";

/**
 * Maximum number of {@link OutputLine} entries retained in {@link AgentSession.output}.
 *
 * When the buffer exceeds this limit the oldest lines are dropped from the front,
 * keeping memory use bounded for long-running sessions.
 *
 * @docLink packages/sdk/concepts#agent-session
 */
export const MAX_OUTPUT_LINES = 500;

interface MinimalRuntime {
  prompt(message: string): Promise<void>;
  reply(answer: string): Promise<void>;
  abort(): Promise<void>;
  dispose(): Promise<void>;
  onEvent(handler: (e: AgentEvent) => void): void;
  offEvent(handler: (e: AgentEvent) => void): void;
}

export class AgentSession {
  state: SessionState = "starting";
  readonly output: OutputLine[] = [];
  onChange?: () => void;

  /** Pointer to the streaming text line being accumulated in-place. */
  private currentTextLine: (OutputLine & { type: "text" }) | null = null;
  /** Registered in constructor so we can offEvent on stop. */
  private readonly eventHandler: (e: AgentEvent) => void;
  /** Optional cleanup hook registered by the factory (temp dir removal, etc.). */
  private cleanupFn?: () => void;

  constructor(protected readonly runtime: MinimalRuntime) {
    this.eventHandler = (e) => this.handleEvent(e);
    runtime.onEvent(this.eventHandler);
  }

  // ── Commands ──────────────────────────────────────────────────────────────

  /** Send a user message. Routes to reply() or prompt() based on state. */
  send(text: string): void {
    if (this.state === "stopped" || this.state === "starting") return;
    this.appendLine({ type: "user", content: text });
    if (this.state === "waiting") {
      this.state = "active";
      void this.runtime.reply(text);
    } else {
      this.state = "active";
      void this.runtime.prompt(text);
    }
  }

  /** Stop the session and dispose the runtime. */
  stop(): void {
    if (this.state === "stopped") return;
    this.state = "stopped";
    this.runtime.offEvent(this.eventHandler);
    void this.runtime.dispose();
    this.appendLine({ type: "system", message: "Session stopped." });
    this.notify();
    this.cleanupFn?.();
  }

  /** Register a cleanup function called when the session stops. Used by the factory. */
  _registerCleanup(fn: () => void): void {
    this.cleanupFn = fn;
  }

  // ── Event → OutputLine ────────────────────────────────────────────────────

  protected handleEvent(event: AgentEvent): void {
    switch (event.type) {
      case "text":
        this.handleTextEvent(event);
        break;
      case "tool_call":
        this.handleToolCallEvent(event);
        break;
      case "question":
        this.handleQuestionEvent(event);
        break;
      case "error":
        this.handleErrorEvent(event);
        break;
      case "status":
        this.handleStatusEvent(event);
        break;
      case "subagent":
        this.handleSubagentEvent(event);
        break;
      case "finished":
        this.handleFinishedEvent();
        break;
      case "state_changed":
        this.handleStateChangedEvent(event);
        break;
    }

    this.notify();
  }

  private handleTextEvent(event: AgentEvent): void {
    const content = "content" in event ? String(event.content) : "";
    if (this.currentTextLine) {
      this.currentTextLine.content += content;
    } else {
      const line: OutputLine & { type: "text" } = { type: "text", content };
      this.output.push(line);
      this.currentTextLine = line;
      this.trimOutput();
    }
    this.state = "active";
  }

  private handleToolCallEvent(event: AgentEvent): void {
    this.sealText();
    const name =
      "name" in event ? String(event.name) : "tool" in event ? String(event.tool) : "tool";
    this.appendLine({ type: "tool", name });
    this.state = "active";
  }

  private handleQuestionEvent(event: AgentEvent): void {
    this.sealText();
    const e = event as Record<string, unknown>;
    const text =
      "question" in e
        ? String(e.question)
        : "text" in e
          ? String(e.text)
          : "The agent has a question.";
    this.appendLine({ type: "question", text });
    this.state = "waiting";
  }

  private handleErrorEvent(event: AgentEvent): void {
    this.sealText();
    const message = "message" in event ? String(event.message) : "An error occurred.";
    this.appendLine({ type: "error", message });
    this.state = "idle";
  }

  private handleStatusEvent(event: AgentEvent): void {
    const phase = "phase" in event ? String(event.phase) : "";
    if (phase === "thinking" && this.currentTextLine !== null) return;
    if (phase) this.appendLine({ type: "status", phase });
  }

  private handleSubagentEvent(event: AgentEvent): void {
    this.sealText();
    const e = event as Record<string, unknown>;
    const name = "name" in e ? String(e.name) : "unknown";
    const status = "status" in e ? (String(e.status) as "started" | "finished") : "started";
    const description = "description" in e ? String(e.description) : "";
    this.appendLine({ type: "subagent", name, status, description });
    if (status === "started") {
      this.state = "active";
    }
  }

  private handleFinishedEvent(): void {
    this.sealText();
    this.appendLine({ type: "separator" });
    this.state = "idle";
  }

  private handleStateChangedEvent(event: AgentEvent): void {
    if ("state" in event && (event as Record<string, unknown>).state === "stopped") {
      this.sealText();
      this.appendLine({ type: "system", message: "Agent stopped." });
      this.state = "stopped";
    }
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  protected appendLine(line: OutputLine): void {
    this.output.push(line);
    this.trimOutput();
  }

  protected notify(): void {
    this.onChange?.();
  }

  private sealText(): void {
    this.currentTextLine = null;
  }

  private trimOutput(): void {
    while (this.output.length > MAX_OUTPUT_LINES) {
      this.output.shift();
    }
  }
}
